// Tabs.jsx — VMFIVE Design System
// Underline-style tab bar.
//
// Usage:
//   import { Tabs } from '../components/ds';
//
//   <Tabs
//     items={[
//       { key: 'overview', label: '總覽' },
//       { key: 'settings', label: '設定' },
//     ]}
//     activeKey="overview"
//     onChange={(key) => setActive(key)}
//   />

import { useRef } from 'react';
import './Tabs.css';

/**
 * Tabs — underline-style tab navigation bar.
 *
 * @param {Object}   props
 * @param {Array}    props.items
 *   Array of tab descriptors. Each entry:
 *   - {string} key      Unique identifier passed to onChange.
 *   - {string} label    Visible text rendered inside the tab button.
 *   - {boolean} [disabled] When true the tab is un-clickable.
 * @param {string}   props.activeKey    The `key` of the currently active tab.
 * @param {Function} props.onChange     Called with the tab's `key` when clicked.
 * @param {string}   [props.className]  Extra class names on the outer element.
 */
export default function Tabs({ items = [], activeKey, onChange, className = '' }) {
  const listRef = useRef(null);

  /**
   * Arrow-key keyboard navigation between tabs.
   * Left/Right move focus; Home/End jump to first/last.
   */
  function handleKeyDown(e, index) {
    const buttons = Array.from(listRef.current.querySelectorAll('[role="tab"]:not([disabled])'));
    const currentIdx = buttons.indexOf(e.currentTarget);

    let next = -1;
    if (e.key === 'ArrowRight') {
      next = (currentIdx + 1) % buttons.length;
    } else if (e.key === 'ArrowLeft') {
      next = (currentIdx - 1 + buttons.length) % buttons.length;
    } else if (e.key === 'Home') {
      next = 0;
    } else if (e.key === 'End') {
      next = buttons.length - 1;
    }

    if (next !== -1) {
      e.preventDefault();
      buttons[next].focus();
    }
  }

  return (
    <div
      role="tablist"
      aria-label="Tabs"
      ref={listRef}
      className={`ds-tabs ${className}`.trim()}
    >
      {items.map((item, index) => {
        const isActive = item.key === activeKey;
        return (
          <button
            key={item.key}
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            disabled={item.disabled || false}
            className={`ds-tabs__btn${isActive ? ' ds-tabs__btn--active' : ''}`}
            onClick={() => !item.disabled && onChange && onChange(item.key)}
            onKeyDown={(e) => handleKeyDown(e, index)}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
